import { useState } from 'react'
import { NavigateFunction, useNavigate } from 'react-router-dom'
import { trackEvent } from '../../analytics'
import { ContextValue, useAppContext } from 'src/Context'
import { routes } from 'src/routes'
import { API_URL } from 'src/util'
import { Button } from 'src/wizard/Button/Button'
import {
  SectionTitle,
  SectionTitleSize,
  SectionTitleStyle
} from '../SectionTitle/SectionTitle'
import {
  WizardResultCard,
  WizardResult
} from '../WizardResultCard/WizardResultCard'

import styles from './ContractorsResult.module.css'
import { ContractorPriceChart } from 'src/wizard/WizardChart/WizardChart'
import { BenchmarkBasis, FrameworkLike, ChoiceLike } from '../BenchmarkBasis/BenchmarkBasis'

type ContractorsResultType = {
  other: () => void
  submission: string | null
  // wizard: WizardData
  results: WizardResult[]
  budget?: number
  budgetLabel?: string
  amountLabel?: string
  frameworkName?: string
  projectName?: string
  framework?: FrameworkLike | null
  choices?: ChoiceLike[] | null
}

function goAhead(
  navigate: NavigateFunction,
  context: ContextValue,
  submission: string,
  result: WizardResult
) {
  if (context.user === null) return

  const data = new URLSearchParams()
  data.append('username', context.user.username as string)
  data.append('submission', submission)
  data.append('contractor', result.contractorId)
  data.append('service', result.service)
  data.append('specification', JSON.stringify(result.specification))

  fetch(`${API_URL}wizard/select`, {
    method: 'post',
    headers: {
      Authorization: `Token ${context.user.token}`
    },
    body: data
  })

  context.setContext({ ...context, selection: result })
  trackEvent('wizard_select', { contractor: result.contractor, service: result.service })

  if (routes.wizard.children) navigate(routes.wizard.children.summary.path)
}

function email(
  context: ContextValue,
  submission: string,
  setSentSummary: React.Dispatch<React.SetStateAction<boolean>>,
  setEmailError: React.Dispatch<React.SetStateAction<boolean>>
) {
  if (context.user === null) return

  const data = new URLSearchParams()
  data.append('username', context.user.username as string)
  data.append('submission', submission)

  fetch(`${API_URL}wizard/summary`, {
    method: 'post',
    headers: {
      Authorization: `Token ${context.user.token}`
    },
    body: data
  }).then((response) => {
    if (response.ok) {
      setSentSummary(true)
      trackEvent('wizard_email')
    } else {
      setEmailError(true)
    }
  }).catch(() => {
    setEmailError(true)
  })
}

function ContractorsResult(props: ContractorsResultType) {
  const context = useAppContext()
  const navigate = useNavigate()
  const [sentSummary, setSentSummary] = useState(false)
  const [emailError, setEmailError] = useState(false)

  return (
    <div>
      <div className={styles.container}>
        <SectionTitle
          title={props.frameworkName ? `BENCHMARK RESULTS — ${props.frameworkName}` : 'BENCHMARK RESULTS'}
          size={SectionTitleSize.Small}
          style={SectionTitleStyle.LightBlue}
        />
        {props.projectName ? (
          <div className={styles.count} style={{ fontWeight: 600 }}>
            Project: {props.projectName}
          </div>
        ) : null}
        <div className={styles.row}>
          <div className={styles.count}>
            <span className={styles.total}>{props?.results?.length ?? 0}</span>{' '}
            CONTRACTOR{props?.results?.length > 1 ? 'S' : ''} FOUND
          </div>
          {props?.results?.length > 0 ? (
            <Button
              label={emailError ? 'Failed to send' : sentSummary ? 'Summary sent' : 'Email Summary'}
              disabled={sentSummary || emailError}
              onClick={(event) => {
                event.preventDefault()
                email(context, props.submission as string, setSentSummary, setEmailError)
              }}
            />
          ) : null}
        </div>

        <ContractorPriceChart
          data={(props?.results ?? []).map((r) => ({
            contractor: r.contractor,
            amount: r.amount
          }))}
        />

        <BenchmarkBasis
          framework={props.framework}
          wizard={context.wizard}
          choices={props.choices}
        />

        <div>
          {props?.results
            ?.sort((a, b) => a.amount - b.amount)
            .map((result, index) => {
              return (
                <WizardResultCard
                  key={index}
                  {...result}
                  position={index + 1}
                  budget={props.budget}
                  budgetLabel={props.budgetLabel}
                  amountLabel={props.amountLabel}
                  goAhead={() =>
                    goAhead(
                      navigate,
                      context,
                      props.submission as string,
                      result
                    )
                  }
                  other={
                    context.wizard.route === 'contractor'
                      ? props.other
                      : undefined
                  }
                />
              )
            })}
        </div>
      </div>
    </div>
  )
}
export { ContractorsResult }
